import { type MouseEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import {
  useIsAudioNotePaused,
  useIsAudioNoteReconnecting,
  useIsAudioNoteSaving,
  useIsAudioNoteStarting,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useAudioNoteSettings } from '@renderer/chat/hooks/useAudioNoteSettings'
import { useHasTranscript } from '@renderer/hooks/useHasTranscript'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useIsMiniHovered } from '../../../atoms'
import { useIsMiniAnchoredLeft } from '../side-context'
import { MINI_REVEAL_TRANSITION, miniRevealVariants } from '../side-motion'
import { Button, Tooltip } from '@highlight/ui'
import { Play, Pause, Square } from 'lucide-react'

interface MiniControlsProps {
  forceShow?: boolean
}

/** Pause/resume and stop for the recording in progress; `trigger` names the control set in `Audio Note Action`. */
function useRecordingControls(trigger: 'notch' | 'mini') {
  const isPaused = useIsAudioNotePaused()
  const isReconnecting = useIsAudioNoteReconnecting()
  const { isUsingLocalTranscription } = useAudioNoteSettings()
  const hasTranscript = useHasTranscript()

  const handlePlayPause = async (e: MouseEvent) => {
    e.stopPropagation()
    AmplitudeRenderer.trackEvent('Audio Note Action', { action: isPaused ? 'resume' : 'pause', trigger })
    if (isPaused) {
      await AudioNoteIpcRenderer.resumeRecording()
    } else {
      await AudioNoteIpcRenderer.pauseRecording()
    }
  }

  const handleStop = async (e: MouseEvent) => {
    e.stopPropagation()
    // Always request a save and let the main process decide: stopAndSave()
    // checks the authoritative TranscriptManager and discards on its own when
    // there are no entries. Passing the renderer's hasTranscript here would
    // race against transcript entries that haven't synced to global state yet
    // (~500ms), discarding real data. Either way the recording stops — never
    // open the drawer here.
    AmplitudeRenderer.trackEvent('Audio Note Action', { action: 'stop', trigger, hasTranscript })
    await AudioNoteIpcRenderer.stopRecording(true)
  }

  return { isPaused, isReconnecting, isUsingLocalTranscription, hasTranscript, handlePlayPause, handleStop }
}

/** The redesigned notch's Pause and Stop icons, beside the meeting's elapsed time. */
export function MiniNotchControls() {
  const { isPaused, isReconnecting, isUsingLocalTranscription, hasTranscript, handlePlayPause, handleStop } =
    useRecordingControls('notch')
  const showsPlay = isPaused || isReconnecting

  return (
    <div className="flex items-center">
      <Tooltip.Provider>
        {!isUsingLocalTranscription && (
          <Tooltip disabled={isReconnecting}>
            <Tooltip.Trigger
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  shape="square"
                  tone="on-dark"
                  aria-label={showsPlay ? 'Resume' : 'Pause'}
                  onClick={handlePlayPause}
                  disabled={isReconnecting}
                >
                  {showsPlay ? <Play /> : <Pause />}
                </Button>
              }
            />
            <Tooltip.Content sideOffset={12}>{showsPlay ? 'Resume' : 'Pause'}</Tooltip.Content>
          </Tooltip>
        )}
        <Tooltip>
          <Tooltip.Trigger
            render={
              <Button variant="ghost" size="sm" shape="square" tone="on-dark" aria-label="Stop" onClick={handleStop}>
                <Square />
              </Button>
            }
          />
          <Tooltip.Content sideOffset={12}>{hasTranscript ? 'End and Save' : 'Stop'}</Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
    </div>
  )
}

export function MiniControlsButtons() {
  const isOnLeftSide = useIsMiniAnchoredLeft()
  const { isPaused, isReconnecting, isUsingLocalTranscription, hasTranscript, handlePlayPause, handleStop } =
    useRecordingControls('mini')

  return (
    <div className={twMerge('flex items-center gap-0.5', !isOnLeftSide && 'flex-row-reverse')}>
      {/* Play/Pause Button - hidden when using local transcription */}
      <Tooltip.Provider>
        {!isUsingLocalTranscription && (
          <Tooltip disabled={isReconnecting}>
            <Tooltip.Trigger
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  shape="square"
                  aria-label={isPaused ? 'Resume' : 'Pause'}
                  onClick={handlePlayPause}
                  disabled={isReconnecting}
                >
                  {isPaused || isReconnecting ? (
                    <Play className="text-foreground opacity-70" fill="currentColor" />
                  ) : (
                    <Pause className="text-foreground opacity-70" fill="currentColor" />
                  )}
                </Button>
              }
            />
            <Tooltip.Content sideOffset={12}>{isPaused ? 'Resume' : 'Pause'}</Tooltip.Content>
          </Tooltip>
        )}
        <Tooltip>
          <Tooltip.Trigger
            render={
              <Button variant="ghost" size="sm" shape="square" aria-label="Stop" onClick={handleStop}>
                <Square className="text-foreground opacity-70" fill="currentColor" />
              </Button>
            }
          />
          <Tooltip.Content sideOffset={12}>{hasTranscript ? 'End and Save' : 'Stop'}</Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
    </div>
  )
}

export function MiniControls({ forceShow }: MiniControlsProps) {
  const isHovered = useIsMiniHovered()
  const isSaving = useIsAudioNoteSaving()
  const isStarting = useIsAudioNoteStarting()
  const showControls = (forceShow || isHovered) && !isSaving && !isStarting
  const isAnchoredLeft = useIsMiniAnchoredLeft()

  if (forceShow) {
    return showControls ? <MiniControlsButtons /> : null
  }

  return (
    <AnimatePresence>
      {showControls && (
        <motion.div
          variants={miniRevealVariants(isAnchoredLeft)}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={MINI_REVEAL_TRANSITION}
        >
          <MiniControlsButtons />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
